import {
  AnchorHTMLAttributes, ButtonHTMLAttributes, CSSProperties, ElementType,
  Fragment, HTMLAttributes, InputHTMLAttributes, ReactNode,
  SelectHTMLAttributes, TextareaHTMLAttributes, forwardRef,
} from "react";
import { AlertTriangle, ArrowLeft, ChevronRight, CircleHelp, Inbox } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { hatEigeneHilfe, useHilfe } from "../features/hilfe";

/* ─────────────────────────────────────────────────────────────────────
   Die Bausteine der Oberfläche.

   Eine Regel: hier steht kein Aussehen, nur Struktur. Alles Sichtbare
   kommt aus `ui.css`, alle Farben aus den Tokens in `index.css`. Wer eine
   Kante runder oder einen Knopf ruhiger haben will, ändert eine Zeile CSS
   und trifft damit jede Stelle im Projekt gleichzeitig — genau das ist der
   Zweck dieser Datei.

   Jede Komponente reicht ihre restlichen Attribute weiter und hängt eine
   mitgegebene `className` hinten an. So bleibt jede Stelle anpassbar,
   ohne dass jemand eine Variante in die Bibliothek zurückschreiben muss.
   ───────────────────────────────────────────────────────────────────── */

/** Klassennamen zusammensetzen; alles Falsche fällt raus. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Ein Palettenname aus den Tokens — z. B. "blue", "green", "mauve". */
export type Ton =
  | "accent" | "accent-2" | "accent-3"
  | "info" | "info-2" | "info-3"
  | "success" | "warning" | "warning-2" | "danger" | "danger-2" | "soft"
  | "muted" | "text";

/** Setzt die beiden Variablen, aus denen Plaketten und Glyphen ihre Farbe
 *  ziehen. Ein Ton reicht — Volltonfarbe und RGB-Tripel folgen daraus. */
export function tonStyle(ton?: Ton): CSSProperties {
  if (!ton) return {};
  return {
    ["--ui-glyph-color" as string]: `var(--${ton})`,
    ["--ui-glyph-rgb" as string]: `var(--${ton}-rgb)`,
    ["--ui-badge-color" as string]: `var(--${ton})`,
    ["--ui-badge-rgb" as string]: `var(--${ton}-rgb)`,
  };
}

/* ───────────────────────────── Seite ───────────────────────────── */

/** Der Rahmen einer Seite.
 *
 *  Ohne Breiten-Regler: es gab einmal einen, und das Ergebnis waren drei
 *  verschiedene Seitenbreiten und Überschriften, die je nach Seite an einer
 *  anderen Stelle standen. Die Breite gehört in `ui.css` und gilt für alle. */
export function Page({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-page", className)} {...rest}>{children}</div>;
}

export function PageHeader({
  titel, icon, sub, aktionen, zurueck, className, ...rest
}: HTMLAttributes<HTMLElement> & {
  titel: ReactNode;
  icon?: ReactNode;
  /** Kurze Einordnung. Weglassen, wenn der Titel für sich spricht — die
   *  Oberfläche soll nicht erklären, was man ohnehin sieht. */
  sub?: ReactNode;
  aktionen?: ReactNode;
  /** Der Weg zurück zur Übersicht, links vor dem Titel.
   *
   *  Dort und nirgends sonst: wer eine Unterseite offen hat, will als Erstes
   *  wieder heraus, und sucht dafür oben links. Als letzter Knopf rechts
   *  zwischen drei Marken findet ihn niemand — genau so stand er vorher auf
   *  der Repo-Seite, und auf der Seiten-Detailansicht gab es ihn gar nicht. */
  zurueck?: { to: string; titel: string };
}) {
  const hilfe = useHilfe();
  const { pathname } = useLocation();
  // Nur, wo es wirklich etwas zu lesen gibt. Unterseiten erben die Hilfe
  // ihres Bereichs — /git/mein-projekt fuehrt in die von /git.
  const hilfePfad = hatEigeneHilfe(pathname) ? pathname : null;
  return (
    <header className={cx("ui-page-head", className)} {...rest}>
      {zurueck && (
        <Link to={zurueck.to} className="ui-page-zurueck" title={zurueck.titel}
          aria-label={zurueck.titel}>
          <ArrowLeft className="icon-svg" />
        </Link>
      )}
      <div className="ui-page-head-text">
        {/* Die Überschrift ist der Weg in die Hilfe zu *dieser* Seite.
            Vorher gab es nur den Eintrag unten in der Leiste, der das
            ganze Buch aufschlug und dann zum richtigen Kapitel sprang —
            also dreimal so weit weg wie die Frage, die man gerade hat.
            Wo es keine Hilfe gibt, bleibt es eine schlichte Überschrift:
            ein Knopf, der nichts tut, ist schlimmer als kein Knopf. */}
        <h1 className="ui-page-title">
          {hilfePfad ? (
            <button type="button" className="ui-page-title-knopf"
              title="What this page does" onClick={() => hilfe.oeffnen(hilfePfad)}>
              {icon}{titel}
              <CircleHelp className="icon-svg ui-page-title-hilfe" aria-hidden="true" />
            </button>
          ) : (
            <>{icon}{titel}</>
          )}
        </h1>
        {sub && <p className="ui-page-sub">{sub}</p>}
      </div>
      {aktionen && <div className="ui-page-actions">{aktionen}</div>}
    </header>
  );
}

export function SectionLabel({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-section-label", className)} {...rest}>{children}</div>;
}

/* ───────────────────────────── Knöpfe ───────────────────────────── */

export type ButtonVariante = "default" | "primary" | "ghost" | "danger";

type ButtonEigen = {
  variante?: ButtonVariante;
  /** Nur ein Symbol, quadratisch. */
  icon?: boolean;
  klein?: boolean;
  block?: boolean;
};

function buttonKlasse({ variante = "default", icon, klein, block }: ButtonEigen, extra?: string) {
  return cx(
    "ui-btn",
    variante !== "default" && `ui-btn--${variante}`,
    icon && "ui-btn--icon",
    klein && "ui-btn--sm",
    block && "ui-btn--block",
    extra,
  );
}

/** Macht aus `title` ein `data-tip`.
 *
 *  Der Grund: solange ein Element ein `title` trägt, zeigt der Browser seine
 *  eigene graue Blase — zusätzlich zu unserer. Die Schicht in <TooltipLayer>
 *  räumt ein `title` beim ersten Überfahren weg, aber React setzt es wieder,
 *  sobald sich der Text ändert („Show the tree" ⇄ „Hide the tree"), und dann
 *  stehen beide da.
 *
 *  Deshalb entsteht es hier gar nicht erst. Alles, was durch den Baukasten
 *  geht, trägt von vornherein `data-tip`; für Fremdkörper bleibt die Schicht
 *  als Auffangnetz. */
function alsTip<T extends { title?: string }>(props: T): Omit<T, "title"> & { "data-tip"?: string } {
  const { title, ...rest } = props;
  return title ? { ...rest, "data-tip": title } : (rest as Omit<T, "title">);
}

export const Button = forwardRef<HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & ButtonEigen>(
  function Button({ variante, icon, klein, block, className, type = "button", ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={buttonKlasse({ variante, icon, klein, block }, className)}
        {...alsTip(rest)}
      />
    );
  });

/** Derselbe Knopf als Link — für alles, was eine echte Adresse hat. */
export function ButtonLink({
  variante, icon, klein, block, className, ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & ButtonEigen) {
  return <a className={buttonKlasse({ variante, icon, klein, block }, className)} {...alsTip(rest)} />;
}

export function ButtonRow({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-btn-row", className)} {...rest}>{children}</div>;
}

/* ───────────────────────────── Karten ───────────────────────────── */

export function CardGrid({
  min, className, children, ...rest
}: HTMLAttributes<HTMLDivElement> & { min?: "sm" | "md" | "lg" }) {
  const mod = min === "sm" ? " ui-cards--sm" : min === "lg" ? " ui-cards--lg" : "";
  return <div className={cx("ui-cards" + mod, className)} {...rest}>{children}</div>;
}

type CardEigen = {
  /** `as` erlaubt dieselbe Karte als <a>, <button> oder <Link>. */
  as?: ElementType;
  klickbar?: boolean;
  gewaehlt?: boolean;
  gestrichelt?: boolean;
  schmal?: boolean;
};

export function Card({
  as: Tag = "div", klickbar, gewaehlt, gestrichelt, schmal, className, children, ...rest
}: CardEigen & Record<string, unknown>) {
  return (
    <Tag
      className={cx(
        "ui-card",
        klickbar && "ui-card--clickable",
        gewaehlt && "ui-card--picked",
        gestrichelt && "ui-card--dashed",
        schmal && "ui-card--pad-sm",
        className as string,
      )}
      {...rest}
    >
      {children as ReactNode}
    </Tag>
  );
}

export function CardActions({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-card-actions", className)} {...rest}>{children}</div>;
}

/** Farbige Symbolplakette. `ton` färbt Symbol und — mit `tint` — den Grund. */
export function Glyph({
  ton, groesse, tint, className, children, style, ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  ton?: Ton; groesse?: "sm" | "md" | "lg"; tint?: boolean;
}) {
  const mod = groesse === "sm" ? " ui-glyph--sm" : groesse === "lg" ? " ui-glyph--lg" : "";
  return (
    <span
      className={cx("ui-glyph" + mod, tint && "ui-glyph--tint", className)}
      style={{ ...tonStyle(ton), ...style }}
      {...rest}
    >
      {children}
    </span>
  );
}

/* ─────────────────────────── Zeilenliste ─────────────────────────── */

export function List({
  auswaehlend, beruehrung, className, children, ...rest
}: HTMLAttributes<HTMLDivElement> & { auswaehlend?: boolean; beruehrung?: boolean }) {
  return (
    <div
      className={cx("ui-list", auswaehlend && "ui-list--selecting", beruehrung && "ui-list--touch", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function ListHead({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-list-head", className)} {...rest}>{children}</div>;
}

export function Tile({
  as: Tag = "div", klickbar, gewaehlt, className, children, ...rest
}: { as?: ElementType; klickbar?: boolean; gewaehlt?: boolean } & Record<string, unknown>) {
  return (
    <Tag
      className={cx("ui-tile", klickbar && "ui-tile--clickable", gewaehlt && "ui-tile--picked", className as string)}
      {...alsTip(rest as { title?: string })}
    >
      {children as ReactNode}
    </Tag>
  );
}

export function TileActions({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-tile-actions", className)} {...rest}>{children}</div>;
}

/* ────────────────────────── Brotkrumen ─────────────────────────── */

export type Krume = { label: ReactNode; onClick?: () => void };

export function Breadcrumbs({ pfad, className, ...rest }:
  HTMLAttributes<HTMLElement> & { pfad: Krume[] }) {
  return (
    <nav className={cx("ui-crumbs", className)} {...rest}>
      {pfad.map((k, i) => {
        const letzte = i === pfad.length - 1;
        return (
          <Fragment key={i}>
            {i > 0 && <span className="ui-crumb-sep"><ChevronRight /></span>}
            <button
              type="button"
              className={cx("ui-crumb", letzte && "ui-crumb--current")}
              onClick={letzte ? undefined : k.onClick}
              disabled={letzte || !k.onClick}
            >
              {k.label}
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
}

export function Toolbar({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-toolbar", className)} {...rest}>{children}</div>;
}

export function Spacer() { return <div className="ui-toolbar-spacer" />; }

/* ───────────────────────── Auswahl-Leiste ───────────────────────── */

export function SelectionBar({
  anzahl, onClear, className, children, ...rest
}: HTMLAttributes<HTMLDivElement> & { anzahl: number; onClear?: () => void }) {
  if (anzahl === 0) return null;
  return (
    <div className={cx("ui-selbar", className)} {...rest}>
      <span className="ui-selbar-count">{anzahl} selected</span>
      <div className="ui-selbar-spacer" />
      {children}
      {onClear && <Button variante="ghost" klein onClick={onClear}>Clear</Button>}
    </div>
  );
}

export function Check({ reveal, className, ...rest }:
  InputHTMLAttributes<HTMLInputElement> & { reveal?: boolean }) {
  return (
    <input
      type="checkbox"
      className={cx("ui-check", reveal && "ui-check--reveal", className)}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    />
  );
}

/* ───────────────────────────── Felder ───────────────────────────── */

/** Beschriftung, Bedienelement, Hinweis — untereinander.
 *
 *  Und ausdrücklich *kein* `<label>`. Ein Label ohne `for` gehört zum
 *  ersten bedienbaren Element darin, und ein `<button>` ist eines: bei
 *  einem Umschalter mit drei Segmenten hing das Label damit am ersten.
 *  Die Folge war, dass es mitgehovert wurde, sobald die Maus irgendwo im
 *  Feld stand — und ein Klick auf die Beschriftung drückte es.
 *  Nachgemessen: über Segment 1 meldeten Segment 0 *und* 1 `:hover`.
 *
 *  Eingabefelder verlieren dadurch, dass ein Klick auf die Beschriftung
 *  sie nicht mehr fokussiert. Das ist der kleinere Verlust: ein Schalter,
 *  der auf fremden Klick reagiert, ist ein Fehler, ein Label, das nicht
 *  fokussiert, nur eine fehlende Bequemlichkeit. */
export function Field({
  label, hint, className, children, ...rest
}: HTMLAttributes<HTMLDivElement> & { label?: ReactNode; hint?: ReactNode }) {
  return (
    <div className={cx("ui-field", className)} {...rest}>
      {label && <span className="ui-field-label">{label}</span>}
      {children}
      {hint && <span className="ui-field-hint">{hint}</span>}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(
  function Input({ mono, className, ...rest }, ref) {
    return <input ref={ref} className={cx("ui-input", mono && "ui-input--mono", className)} {...rest} />;
  });

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return <select ref={ref} className={cx("ui-select", className)} {...rest} />;
  });

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx("ui-textarea", className)} {...rest} />;
  });

export function SearchField({
  icon, className, ...rest
}: InputHTMLAttributes<HTMLInputElement> & { icon?: ReactNode }) {
  return (
    <div className={cx("ui-search", className)}>
      {icon}
      <Input type="search" {...rest} />
    </div>
  );
}

/* ────────────────────────── Segmentschalter ────────────────────── */

export function Segmented<T extends string>({
  wert, optionen, onChange, className, ...rest
}: Omit<HTMLAttributes<HTMLDivElement>, "onChange"> & {
  wert: T;
  /** `ton` färbt genau dieses Segment — nützlich für Filter, die im Rest der
   *  Seite ohnehin eine Farbe tragen. `farbe` tut dasselbe mit einem freien
   *  Palettennamen, für Dinge, deren Farbe der Benutzer selbst wählt (Module
   *  etwa). Ein Hex-Wert wäre hier falsch: er überlebt keinen Themenwechsel. */
  optionen: ReadonlyArray<{
    wert: T; label: ReactNode; title?: string; ton?: Ton; farbe?: string;
  }>;
  onChange: (w: T) => void;
}) {
  return (
    <div className={cx("ui-segmented", className)} role="tablist" {...rest}>
      {optionen.map((o) => (
        <button
          key={o.wert}
          type="button"
          role="tab"
          aria-selected={o.wert === wert}
          data-tip={o.title}
          className={o.wert === wert ? "is-on" : undefined}
          style={
            o.ton || o.farbe
              ? ({ ["--seg" as string]: `var(--${o.farbe ?? o.ton})` })
              : undefined
          }
          onClick={() => onChange(o.wert)}
        >
          {(o.ton || o.farbe) && <span className="ui-seg-dot" />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────────── Plaketten ─────────────────────────── */

export function Badge({
  ton, tint, className, children, style, ...rest
}: HTMLAttributes<HTMLSpanElement> & { ton?: Ton; tint?: boolean }) {
  return (
    <span
      className={cx("ui-badge", (tint || ton) && "ui-badge--tint", className)}
      style={{ ...tonStyle(ton), ...style }}
      {...rest}
    >
      {children}
    </span>
  );
}

/* ─────────────────────── Leer / Laden / Fehler ──────────────────── */

export function EmptyState({
  titel, icon, aktion, className, children, ...rest
}: HTMLAttributes<HTMLDivElement> & { titel: ReactNode; icon?: ReactNode; aktion?: ReactNode }) {
  return (
    <div className={cx("ui-empty", className)} {...rest}>
      {icon ?? <Inbox />}
      <div className="ui-empty-title">{titel}</div>
      {children}
      {aktion && <div className="ui-empty-actions">{aktion}</div>}
    </div>
  );
}

export function Spinner({ gross, className, ...rest }:
  HTMLAttributes<HTMLSpanElement> & { gross?: boolean }) {
  return <span className={cx("ui-spinner", gross && "ui-spinner--lg", className)} role="status" aria-label="Loading" {...rest} />;
}

export function LoadingRow({ label = "Loading…", className, ...rest }:
  HTMLAttributes<HTMLDivElement> & { label?: ReactNode }) {
  return (
    <div className={cx("ui-loading-row", className)} {...rest}>
      <Spinner />{label}
    </div>
  );
}

/** Dünner Balken über dem Inhalt. Die Seite bleibt stehen und sagt oben,
 *  dass noch etwas unterwegs ist — statt alles gegen einen Kreis zu
 *  tauschen und den Benutzer die Orientierung kosten zu lassen. */
export function LoadingBar({ anteil, className, ...rest }:
  HTMLAttributes<HTMLDivElement> & { anteil?: number }) {
  const unbestimmt = anteil === undefined;
  return (
    <div className={cx("ui-bar", unbestimmt && "ui-bar--indeterminate", className)} {...rest}>
      <i style={unbestimmt ? undefined : { width: `${Math.max(0, Math.min(100, anteil))}%` }} />
    </div>
  );
}

export function ErrorNote({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  if (!children) return null;
  return (
    <div className={cx("ui-error", className)} role="alert" {...rest}>
      <AlertTriangle />
      <span>{children}</span>
    </div>
  );
}

export function Skeleton({ h = 16, w, className, style, ...rest }:
  HTMLAttributes<HTMLDivElement> & { h?: number | string; w?: number | string }) {
  return (
    <div
      className={cx("ui-skeleton", className)}
      style={{ height: typeof h === "number" ? `${h}px` : h, width: typeof w === "number" ? `${w}px` : w, ...style }}
      {...rest}
    />
  );
}

/* ─────────────────────────── Kennzahlen ─────────────────────────── */

export function Stats({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-stats", className)} {...rest}>{children}</div>;
}

export function Stat({
  label, wert, hint, laedt, className, ...rest
}: HTMLAttributes<HTMLDivElement> & {
  label: ReactNode; wert: ReactNode; hint?: ReactNode; laedt?: boolean;
}) {
  return (
    <div className={cx("ui-stat", className)} {...rest}>
      <div className="ui-stat-label">{label}</div>
      <div className="ui-stat-value">{laedt ? <Skeleton h={22} w={64} /> : wert}</div>
      {hint && <div className="ui-stat-hint">{hint}</div>}
    </div>
  );
}
